/**
 * MessageItem Component
 *
 * Displays a single message in a conversation with appropriate styling
 * for user vs assistant messages, handles RTL/LTR text, attachments,
 * status badges, and action buttons.
 */

import React from 'react';
import type { Message, QuestionnaireType } from '@ideoz/core';
import { ChevronRight, useAttachmentDisplay } from '@ideoz/core';
import UserMessage from './user-message';
import IdeozMessage from './ideoz-message';
import { Button } from '../atoms/button';
import { Badge } from '../atoms/badge';
import FilePreview from './file-preview';
import Icon from './Icon';
import {
  Check,
  Info,
  TriangleAlert,
  ThumbsUp,
  ThumbsDown,
  Copy,
  RotateCcw,
  MoreHorizontal,
  ArrowUpWideNarrow,
  ChevronLeft,
} from 'lucide-react';
// Analytics: events tracked via data-track attributes and Redux middleware
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../atoms/tooltip';
import { hasArabicCharacters, copyMarkdownAsHTML } from '../../utils/messageFormatting';

// ============================================================================
// TYPES
// ============================================================================

export interface MessageItemProps {
  conversationId?: string | null;
  projectId?: string | null;
  message: Message;
  isLastMessage: boolean;
  isReflection: boolean;
  isRepeated: boolean;
  isClarification: boolean;
  showCreateButton: boolean;
  shouldShowBanner: boolean;
  isReadyToCreate: boolean;
  showBannerPatient: boolean;
  isNeedTime: boolean;
  canBeDrawn: QuestionnaireType | null;
  readyToGenerate: string | null;
  isCreatingAccount: boolean;
  isStreaming: boolean;
  isLoading: boolean;
  isWeb: boolean;
  isLoggedIn: boolean;
  onPrototyperClick?: (prototyperDemoLinkId?: string | number) => void;

  // Actions
  onSubmit: (text: string) => void;
  onActionClick: (action: string, message?: string) => void;
  onFeedback?: (
    type: 'like' | 'dislike',
    messageID?: string | null,
    projectId?: string | null
  ) => void | Promise<void>;
  setIsReflection: (value: boolean) => void;
  setIsRepeated: (value: boolean) => void;
  onRetryDrawing?: (drawType: string) => void;
  forcedDirection?: 'rtl' | 'ltr';
  isPrototypeOpen?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Detect if text should be RTL (Arabic)
 */
const normalizeDirection = (detectedLanguage?: string): 'rtl' | 'ltr' | undefined => {
  if (!detectedLanguage) return undefined;

  const normalized = detectedLanguage.toLowerCase();
  if (normalized === 'rtl' || normalized === 'ltr') {
    return normalized;
  }

  const matchesCode = (codes: string[]) =>
    codes.some((code) => normalized === code || normalized.startsWith(`${code}-`));

  const rtlLanguages = ['ar', 'he', 'fa', 'ur', 'ku', 'ps', 'sd', 'ug', 'yi'];
  if (
    matchesCode(rtlLanguages) ||
    normalized.includes('arabic') ||
    normalized.includes('right-to-left')
  ) {
    return 'rtl';
  }

  const ltrLanguages = ['en', 'fr', 'de', 'es', 'pt', 'it', 'tr', 'id'];
  if (
    matchesCode(ltrLanguages) ||
    normalized.includes('english') ||
    normalized.includes('left-to-right')
  ) {
    return 'ltr';
  }

  return undefined;
};

const detectRTL = (text: string, detectedLanguage?: string): boolean => {
  const direction = normalizeDirection(detectedLanguage);
  if (direction === 'rtl') return true;
  if (direction === 'ltr') return false;

  if (!text) return false;
  return hasArabicCharacters(text);
};

/**
 * Clean user message to remove attachment markers
 */
const cleanUserMessage = (content: string): string => {
  if (!content) return '';
  // Remove attachment information like "Attachments: file1.pdf, file2.txt"
  return content.replace(/Attachments?:\s*[^\n]+/gi, '').trim();
};

// ============================================================================
// COMPONENT
// ============================================================================

export const MessageItem: React.FC<MessageItemProps> = ({
  conversationId,
  projectId,
  message,
  isLastMessage,
  isReflection,
  isRepeated,
  isClarification,
  showCreateButton,
  shouldShowBanner,
  isReadyToCreate,
  showBannerPatient,
  isNeedTime,
  canBeDrawn,
  readyToGenerate,
  isCreatingAccount,
  isStreaming,
  isLoading,
  isWeb,
  isLoggedIn,
  onPrototyperClick,
  onSubmit,
  onActionClick,
  onFeedback,
  setIsReflection,
  setIsRepeated,
  onRetryDrawing,
  forcedDirection,
  isPrototypeOpen,
}) => {
  // ========== State ==========
  const [feedback, setFeedback] = React.useState<'none' | 'like' | 'dislike'>(
    // Initialize from message.isLiked if available
    (message as any).isLiked === true
      ? 'like'
      : (message as any).isLiked === false
        ? 'dislike'
        : 'none'
  );
  const [isCopied, setIsCopied] = React.useState(false);
  const [isSendingFeedback, setIsSendingFeedback] = React.useState(false);

  // ========== Effects ==========

  // Update feedback state when message.isLiked changes
  React.useEffect(() => {
    const messageIsLiked = (message as any).isLiked;
    const newFeedback =
      messageIsLiked === true ? 'like' : messageIsLiked === false ? 'dislike' : 'none';

    if (newFeedback !== feedback) {
      setFeedback(newFeedback);
    }
  }, [(message as any).isLiked]);

  // ========== Fetch Attachments ==========

  const { attachments: coreAttachments, isLoading: isLoadingAttachments } = useAttachmentDisplay({
    attachmentIds: (message as any).attachments_ids || [],
  });

  // Convert core attachments to UI format
  const attachments = coreAttachments.map((att) => ({
    id: att.id,
    file: new File([], att.name, { type: att.type }),
    preview: att.preview,
    isImage: att.isImage,
    isUploading: false,
    uploadProgress: 100,
  }));

  // ========== Derived State ==========

  const computedIsRtl = detectRTL(message.content || '', (message as any).detectedLanguage);
  const isRtl = forcedDirection ? forcedDirection === 'rtl' : computedIsRtl;

  const hasAttachments = (message as any).attachments_ids?.length > 0;

  // ========== Handlers ==========

  const handleActionClick = (text: string) => {
    onSubmit(text);
    setIsReflection(false);
    setIsRepeated(false);
  };

  const handleRetryDrawing = () => {
    if (onRetryDrawing && (message as any).drawType) {
      // Analytics: drawing.creation.retried tracked via data-track attribute
      onRetryDrawing((message as any).drawType);
    }
  };

  const handleFeedback = async (type: 'like' | 'dislike') => {
    if (!onFeedback) return;

    // Store previous state for potential rollback
    const previousFeedback = feedback;
    const newFeedback = previousFeedback === type ? 'none' : type;

    // Optimistic UI update
    setFeedback(newFeedback);

    setIsSendingFeedback(true);
    try {
      // Use llmInteractionId (real backend UUID) if available, otherwise fall back to id (timestamp)
      const interactionId = (message as any).llmInteractionId || message.id;
      await onFeedback(type, interactionId, projectId);
    } catch (err) {
      console.error('Failed to send feedback', err);
      // Revert optimistic update on error
      setFeedback(previousFeedback);
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const handleCopyMessage = async () => {
    try {
      const content = message.content || '';

      // Copy as HTML (supports all markdown elements: headings, lists, tables, code blocks, etc.)
      // Falls back to plain markdown if HTML paste is not supported
      const success = await copyMarkdownAsHTML(content);

      if (success) {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } else {
        // Final fallback: copy as plain text
        await navigator.clipboard.writeText(content);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy message', err);
    }
  };

  // ========== Render Helpers ==========

  /**
   * Render drawing status banner (success/error)
   */
  const renderBanner = (className?: string) => {
    const bannerType = (message as any).bannerType;
    const drawType = (message as any).drawType;
    const bannerText = (message as any).bannerText;

    if (!bannerType) return null;

    return (
      <div className={`flex items-center w-full ${isRtl ? 'flex-row-reverse' : 'flex-row'}`}>
        <Badge
          variant={bannerType}
          dir={isRtl ? 'rtl' : 'ltr'}
          leftIcon={
            <Icon
              icon={bannerType === 'success' ? Check : TriangleAlert}
              size={18}
              strokeWidth={2}
              color={bannerType === 'success' ? '#067647' : '#D92D20'}
            />
          }
        >
          <div className={`flex items-center flex-1 ${isRtl ? 'gap-2' : 'justify-between'}`}>
            {bannerText ? (
              bannerText
            ) : bannerType === 'success' ? (
              isRtl ? (
                <>
                  لقد أكمل <span style={{ direction: 'ltr', display: 'inline' }}>Ideoz AI</span>{' '}
                  إنشاء{' '}
                  <span style={{ direction: 'ltr', display: 'inline' }}>
                    {drawType?.replace(/_/g, ' ')}
                  </span>{' '}
                  بنجاح
                </>
              ) : drawType === 'prototyper' ? (
                'Ideoz AI has completed creating the prototyper successfully'
              ) : (
                `Ideoz AI has completed the generating ${drawType?.replace(/_/g, ' ')} successfully`
              )
            ) : isRtl ? (
              drawType === 'prototyper' ? (
                'حدث خطأ ما، يرجى المحاولة مرة أخرى'
              ) : (
                'عذراً، شيء لم يسر كما هو مخطط له.'
              )
            ) : drawType === 'prototyper' ? (
              'Something went wrong, please try again'
            ) : (
              "Oops, something didn't go as planned."
            )}

            {bannerType === 'error' &&
              drawType &&
              onRetryDrawing &&
              (drawType === 'prototyper' || !isWeb) && (
                <Button
                  onClick={handleRetryDrawing}
                  variant="link-gray"
                  size="sm"
                  className="py-0 h-2"
                  data-track="try_again"
                >
                  {isRtl ? 'حاول مرة أخرى' : 'Try again'}
                </Button>
              )}
          </div>
        </Badge>
      </div>
    );
  };

  /**
   * Render message content based on role
   */
  const renderMessageContent = () => {
    // User message
    if (message.role === 'user') {
      const cleanedContent = cleanUserMessage(message.content || '');
      const hasText = cleanedContent && cleanedContent.trim();

      if (!hasText && !hasAttachments) return null;

      return (
        <>
          {hasText && (
            <div className="flex justify-end max-w-[80%]">
              <UserMessage dir={isRtl ? 'rtl' : 'ltr'} isPrototypeOpen={isPrototypeOpen}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{cleanedContent}</div>
              </UserMessage>
            </div>
          )}

          {hasAttachments && attachments.length > 0 && (
            <div className="mt-2 ml-7">
              <FilePreview files={attachments as any} hasDelete={false} />
            </div>
          )}
        </>
      );
    }

    // Assistant message
    const hasCreateProject = message.content?.includes('Creating a Project ...');
    const hasCreateFreeAccount = message.content?.includes('Creating an Account ...');

    if (!message.content || hasCreateProject || hasCreateFreeAccount) return null;

    // Special handling for reflection/repeated messages
    if ((isReflection || isRepeated) && isLastMessage) {
      return (
        <IdeozMessage
          isReflection={isReflection}
          isRepeated={isRepeated}
          onActionClick={handleActionClick}
          isRtl={isRtl}
        >
          {message.content}
        </IdeozMessage>
      );
    }

    {
      const messageID = (message as any).id;

      return (
        <div className="flex flex-col max-w-full">
          {(message as any).hasBanner &&
            (message as any).bannerPosition === 'top' &&
            renderBanner('mb-6')}
          <IdeozMessage isRtl={isRtl}>
            {message.content.replace('Creating a Project ...', '')}
          </IdeozMessage>
          {(message as any).hasBanner && (message as any).bannerPosition === 'bottom' && renderBanner('mt-0 mb-6')}
        </div>
      );
    }
  };

  /**
   * Render prototyper banner
   */
  const renderPrototyperBanner = () => {
    const prototyperDemoLinkId = (message as any).prototyper_demo_link_id;
    console.log('prototyperDemoLinkId', prototyperDemoLinkId);
    const messageMode = (message as any).mode;
    // Show banner if message has mode='prototyper' OR has prototyper_demo_link_id
    if (messageMode !== 'prototyper' || !prototyperDemoLinkId) return null;
    if (message.role !== 'assistant') return null;
    // Don't show prototyper banner if there's an error banner
    if ((message as any).hasBanner) return null;
    const handleBannerClick = () => {
      // Read the ID directly from message to avoid closure issues
      const currentPrototyperId = (message as any).prototyper_demo_link_id;

      if (onPrototyperClick) {
        onPrototyperClick(currentPrototyperId);
      }
    };

    return (
      <div
        onClick={handleBannerClick}
        className={`flex items-center justify-between w-[80%] bg-white border border-gray-200 rounded-lg px-4 py-3 ${isRtl ? 'mr-7 ' : 'ml-7'} cursor-pointer hover:bg-gray-50 transition-colors`}
      >
        <div className={`flex items-center gap-2 w-full ${isRtl ? 'flex-row-reverse' : ''}`}>
          <Icon icon={isRtl ? ChevronRight : ChevronLeft} size={20} color="#667085" />
          <span className="text-gray-700 font-medium text-sm">
            {isRtl ? 'تم إنشاء النموذج الأولي' : 'Built prototyper'}
          </span>
        </div>
      </div>
    );
  };

  /**
   * Render feedback controls (like, dislike, copy)
   */
  const renderFeedbackControls = () => {
    // Only show for assistant messages that are not reflection/repeated/clarification
    if (message.role !== 'assistant') return null;
    if (isReflection || isRepeated) return null;
    if (!message.content) return null;
    if ((isLoading || isStreaming) && isLastMessage) return null;
    // Don't show feedback controls if there's an error banner
    if ((message as any).hasBanner && (message as any).bannerType === 'error') return null;

    return (
      <div
className={`flex items-center gap-1 ${isRtl ? 'mr-7 flex-row-reverse' : 'ml-7'} mt-1 mb-2`}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-1 hover:bg-transparent"
                onClick={() => handleFeedback('like')}
                aria-pressed={feedback === 'like'}
                aria-label="Like answer"
                data-testid="btn-like"
                data-track="message_like"
                disabled={isSendingFeedback}
              >
                <Icon
                  icon={ThumbsUp}
                  size={16}
                  color={
                    feedback === 'like' ? '#17B26A' : feedback === 'dislike' ? '#A4A7AE' : '#667085'
                  }
                  fill={feedback === 'like' ? '#17B26A' : 'none'}
                  strokeWidth={2}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Like</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-1 hover:bg-transparent"
                onClick={() => handleFeedback('dislike')}
                aria-pressed={feedback === 'dislike'}
                aria-label="Dislike answer"
                data-testid="btn-dislike"
                data-track="message_dislike"
                disabled={isSendingFeedback}
              >
                <Icon
                  icon={ThumbsDown}
                  size={16}
                  color={
                    feedback === 'dislike' ? '#A4A7AE' : feedback === 'like' ? '#A4A7AE' : '#667085'
                  }
                  fill={feedback === 'dislike' ? '#A4A7AE' : 'none'}
                  strokeWidth={2}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Dislike</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {isWeb && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-1 hover:bg-transparent"
                  onClick={handleCopyMessage}
                  aria-label="Copy full answer"
                  data-testid="btn-copy-answer"
                  data-track="copy_message"
                >
                  <Icon icon={isCopied ? Check : Copy} size={16} strokeWidth={2} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isCopied ? 'Copied!' : 'Copy'}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  };

  /**
   * Render action buttons (Create Project, etc.)
   */
  const renderActionButtons = () => {
    const shouldShowCreateProjectButton =
      showCreateButton &&
      isLastMessage &&
      message.role === 'assistant' &&
      !isReadyToCreate &&
      !isReflection &&
      !isRepeated &&
      !isClarification &&
      !(canBeDrawn && !isWeb) &&
      !isCreatingAccount &&
      !(readyToGenerate && isLoggedIn) &&
      !isStreaming &&
      !isLoading;

    if (!shouldShowCreateProjectButton) return null;

    return (
      <div className={`flex ${isRtl ? 'justify-end mr-7' : 'justify-start ml-7'}`}>
        <Button
          onClick={() => {
            onActionClick('createProject');
            // Analytics: tracked via data-track attributes
          }}
          variant="primary"
        >
          {isRtl
            ? isLoggedIn
              ? 'إنشاء مشروع'
              : 'إنشاء حساب مجاني'
            : isLoggedIn
              ? 'Create Project'
              : 'Create free account'}
        </Button>
      </div>
    );
  };

  /**
   * Render generate buttons (for drawings)
   */
  const renderGenerateButtons = () => {
    // Check if message has x-prototyper header (stored as mode='prototyper' or prototyper flag)
    const hasPrototyperHeader = 
      message.mode === 'prototyper' || 
      (message as any).prototyper === true ||
      message.prototyper_demo_link_id;

    if (
      isClarification ||
      isReflection ||
      isRepeated ||
      isLoading ||
      isCreatingAccount ||
      isStreaming ||
      hasPrototyperHeader
    ) {
      return null;
    }

    if (!isWeb || !readyToGenerate || !isLastMessage || !isLoggedIn) {
      return null;
    }

    const handleGenerateHere = () => {
      // Analytics: prototype.generate_here.clicked tracked via data-track attribute
      const message = isRtl
        ? `إنشاء ${readyToGenerate.replace(/_/g, ' ').replace('node', '')} هنا`
        : `Generate ${readyToGenerate.replace(/_/g, ' ').replace('node', '')} here`;
      onActionClick('generateHere', message);
    };

    const handleDrawingInFigJam = () => {
      // Analytics: prototype.figjam.clicked tracked via data-track attribute
      onActionClick('drawingInFigJam');
    };

    return (
      <div className={`flex gap-3 mt-3 ${isRtl ? 'flex-row-reverse mr-7' : 'flex-row ml-7'}`}>
        <Button onClick={handleGenerateHere} variant="secondary-gray" size="sm" data-track="generate_here">
          {isRtl ? (
            <>
              هنا{' '}
              <span style={{ direction: 'ltr', display: 'inline' }}>
                {readyToGenerate.replace(/_/g, ' ').replace('node', '')}
              </span>{' '}
              إنشاء
            </>
          ) : (
            `Generate ${readyToGenerate.replace(/_/g, ' ').replace('node', '')} here`
          )}
        </Button>
        <Button onClick={handleDrawingInFigJam} variant="secondary-gray" size="sm" data-track="figjam_button">
          {isRtl ? (
            <>
              مباشرة <span style={{ direction: 'ltr', display: 'inline' }}>FigJam</span> الإنشاء على
            </>
          ) : (
            'Generate on FigJam directly'
          )}
        </Button>
      </div>
    );
  };

  /**
   * Render status badges (clarification, etc.)
   */
  const renderStatusBadges = () => {
    if (!isClarification || !isLastMessage) return null;

    return (
      <div className={`flex items-center w-full ${isRtl ? 'flex-row-reverse' : 'flex-row'}`}>
        <Badge
          variant="info"
          dir={isRtl ? 'rtl' : 'ltr'}
          leftIcon={<Icon icon={Info} size={18} strokeWidth={2} color="#0B4A6F" />}
        >
          {isRtl ? (
            <div className="flex gap-2">
              سيواصل <span style={{ direction: 'ltr', display: 'inline' }}>Ideoz</span> العمل بعد
              ردك
            </div>
          ) : (
            'Ideoz will continue working after your reply'
          )}
        </Badge>
      </div>
    );
  };

  // ========== Render ==========
  return (
    <div className={`flex flex-col gap-2 ${isRtl ? 'items-end' : ''}`} key={message.id}>
      <div
        className={`flex ${
          message.role === 'user' ? 'flex-col items-end' : isRtl ? 'justify-end' : 'justify-start'
        }`}
      >
        {renderMessageContent()}
      </div>

      {renderFeedbackControls()}
      {renderPrototyperBanner()}
      {renderStatusBadges()}
      {renderActionButtons()}
      {renderGenerateButtons()}
    </div>
  );
};

export default MessageItem;
